/**
 * Web chat widget utilities - payload builder and data transformer
 */

import { isString } from "lodash";
import { s3uploader } from "./awsS3MediaUploader";
import { WIDGET_CONSTANTS } from "../constant/ChatbotWidgetConstant";

/**
 * Parse metadata JSON string safely
 */
const parseMetadata = metaDataString => {
  try {
    return isString(metaDataString) ? JSON.parse(metaDataString) : metaDataString;
  } catch (error) {
    console.warn("Failed to parse widget metadata:", error);
    return {};
  }
};

const INDICATION_METHOD_CODE = Object.freeze({
  QUESTIONS: "QUESTIONS",
  LOCATIONS: "LOCATIONS",
});

/**
 * Upload widget files to S3 and return URLs
 */
const uploadWidgetFiles = async (formData, companyId) => {
  const uploadPromises = [];
  const uploadResults = {};

  // Upload widget icon if present
  if (formData.appearance?.widgetIcon?.file) {
    const widgetIconPromise = s3uploader(
      formData.appearance.widgetIcon.file,
      `${process.env.GATSBY_S3_BUCKET}${WIDGET_CONSTANTS.S3_PATHS.WIDGET_ASSETS_FOLDER}`,
      () => {}, // loader function - can be empty for now
      companyId || "",
      null,
      null,
      null
    )
      .then(result => {
        uploadResults.widgetIconUrl = result?.url;
      })
      .catch(error => {
        console.error("Widget icon upload failed:", error);
        uploadResults.widgetIconUrl = null;
      });
    uploadPromises.push(widgetIconPromise);
  }

  // Upload launcher icon if present
  if (formData.appearance?.launcherIcon?.file) {
    const launcherIconPromise = s3uploader(
      formData.appearance.launcherIcon.file,
      `${process.env.GATSBY_S3_BUCKET}${WIDGET_CONSTANTS.S3_PATHS.WIDGET_ASSETS_FOLDER}`,
      () => {}, // loader function - can be empty for now
      companyId || "",
      null,
      null,
      null
    )
      .then(result => {
        uploadResults.launcherIconUrl = result?.url;
      })
      .catch(error => {
        console.error("Launcher icon upload failed:", error);
        uploadResults.launcherIconUrl = null;
      });
    uploadPromises.push(launcherIconPromise);
  }

  // Wait for all uploads to complete
  await Promise.all(uploadPromises);
  return uploadResults;
};

/**
 * Build payload for create/update widget
 */
export const buildWidgetPayload = async (formData, widgetName, options = {}) => {
  const { companyId, widgetId, mode, teamId, locationId } = options;

  // Upload files first and get URLs
  const uploadResults = await uploadWidgetFiles(formData, companyId);

  // Base payload - main mutation arguments
  const payload = {
    companyId: companyId,
    isPhoneRequired: false,
    presetQuestionInd: true,
    switchToSmsInd: false,
    indicationMethodCode: INDICATION_METHOD_CODE.QUESTIONS,
  };

  // Add widget ID for update mode
  if (mode === WIDGET_CONSTANTS.WIDGET_MODES.EDIT && widgetId) {
    payload.id = widgetId;
    payload.isActive = true;
  }

  // Add optional main arguments - but preferences teamId takes priority
  if (teamId && !formData.preferences?.forwardConversation?.selectedTeams?.length) {
    payload.teamId = teamId;
  }

  if (locationId) {
    payload.locationId = locationId;
  }

  // Main arguments from form data
  if (formData.appearance?.welcomeMessage?.value) {
    payload.initialText = formData.appearance.welcomeMessage.value;
  }

  if (formData.content?.topics) {
    // Send all topics as array of objects with required shape
    payload.questions = (formData.content.topics || []).map(topic => ({
      isActive: Boolean(topic.enabled),
      question: topic.title || "",
      metaData: {
        welcomeMessage: topic.description || "",
      },
    }));
  }

  if (formData.preferences?.trustedDomains?.domains) {
    payload.origins = formData.preferences.trustedDomains.domains;
  }

  if (formData.preferences?.forwardConversation?.selectedUsers) {
    payload.userIds = formData.preferences.forwardConversation.selectedUsers;
  }

  const { selectedTeams } = formData.preferences?.forwardConversation || {};
  if (selectedTeams?.length > 0) {
    const [firstTeamId] = selectedTeams;
    payload.teamId = firstTeamId;
  }

  // Build metadata - only metadata fields go here
  const metaData = {};

  // Appearance metadata
  if (formData.appearance) {
    const { appearance } = formData;

    if (appearance.widgetHeader?.value) {
      metaData.initialTitle = appearance.widgetHeader.value;
      metaData.widgetTitle = appearance.widgetHeader.value;
    }

    if (appearance.welcomeMessage?.value) {
      metaData.initialDescription = appearance.welcomeMessage.value;
      metaData.miniWidgetText = appearance.welcomeMessage.value;
    }

    if (appearance.widgetIcon?.file && uploadResults.widgetIconUrl) {
      metaData.widgetLogo = uploadResults.widgetIconUrl;
    } else if (appearance.widgetIcon?.preview && !appearance.widgetIcon?.file) {
      // For edit mode, keep existing URL if no new file uploaded
      metaData.widgetLogo = appearance.widgetIcon.preview;
    }

    if (appearance.brandColor?.value) {
      metaData.themeColor = appearance.brandColor.value;
    }

    if (appearance.buttonColor?.value) {
      metaData.buttonColor = appearance.buttonColor.value;
    }

    if (appearance.launcherIcon?.file && uploadResults.launcherIconUrl) {
      metaData.widgetLauncherIcon = uploadResults.launcherIconUrl;
    } else if (appearance.launcherIcon?.preview && !appearance.launcherIcon?.file) {
      // For edit mode, keep existing URL if no new file uploaded
      metaData.widgetLauncherIcon = appearance.launcherIcon.preview;
    }

    if (appearance.launcherText?.value) {
      metaData.widgetLauncherText = appearance.launcherText.value;
    }

    if (appearance.textUsColor?.value) {
      metaData.textUsColor = appearance.textUsColor.value;
    }

    if (appearance.launcherColor?.value) {
      metaData.widgetLauncherColor = appearance.launcherColor.value;
    }

    if (appearance.removeBranding?.value !== undefined) {
      metaData.removeFrameworkBranding = appearance.removeBranding.value;
    }
  }

  // Preferences metadata
  if (formData.preferences?.conversationModes) {
    metaData.showChatUsButton = formData.preferences.conversationModes.chatWithUs;
    metaData.showTextUsButton = formData.preferences.conversationModes.textUs;
  }

  // Ask for location only controls the indication method on the main payload
  if (formData.preferences?.askForLocation?.enabled) {
    payload.indicationMethodCode = INDICATION_METHOD_CODE.LOCATIONS;
  }

  // Show customer service / warranty option is stored in metadata only
  if (formData.preferences?.showLeadHelpWithOptions?.enabled !== undefined) {
    metaData.showLeadHelpWithOptions = !!formData.preferences.showLeadHelpWithOptions.enabled;
  }

  // Default metadata values
  metaData.popupSubmitButtonText = metaData.popupSubmitButtonText || "Send";

  // Add widget name to metadata
  if (widgetName) {
    metaData.widgetName = widgetName;
  }

  if (formData.appearance?.showMessageTextbox?.value !== undefined) {
    metaData.showMessageTextbox = formData.appearance.showMessageTextbox.value;
  }

  // Add metadata to payload
  payload.metaData = metaData;

  return payload;
};

/**
 * Transform widget API response to Redux state format
 */
export const transformWidgetToReduxState = widget => {
  if (!widget) return null;

  const metadata = parseMetadata(widget.metaData);

  // Helper function to get non-empty value or empty string
  const getValueOrEmpty = value => (value && value.trim() !== "" ? value : "");

  // Transform appearance data
  const appearance = {
    widgetIcon: {
      file: null,
      preview: getValueOrEmpty(metadata.widgetLogo) || null,
      error: null,
      isUploading: false,
    },
    widgetHeader: {
      value: getValueOrEmpty(metadata.widgetTitle || metadata.initialTitle),
      error: null,
      isDirty: false,
    },
    welcomeMessage: {
      value: getValueOrEmpty(
        widget.initialText || metadata.initialDescription || metadata.miniWidgetText
      ),
      error: null,
      isDirty: false,
    },
    brandColor: {
      value: getValueOrEmpty(metadata.themeColor),
      error: null,
      isDirty: false,
    },
    buttonColor: {
      value: getValueOrEmpty(metadata.buttonColor),
      error: null,
      isDirty: false,
    },
    launcherIcon: {
      file: null,
      preview: getValueOrEmpty(metadata.widgetLauncherIcon) || null,
      error: null,
      isUploading: false,
    },
    launcherText: {
      value: getValueOrEmpty(metadata.widgetLauncherText),
      error: null,
      isDirty: false,
    },
    textUsColor: {
      value: getValueOrEmpty(metadata.textUsColor),
      error: null,
      isDirty: false,
    },
    launcherColor: {
      value: getValueOrEmpty(metadata.widgetLauncherColor),
      error: null,
      isDirty: false,
    },
    removeBranding: {
      value: metadata.removeFrameworkBranding === true,
      error: null,
      isDirty: false,
    },
    showMessageTextbox: {
      value: metadata.showMessageTextbox === true,
      error: null,
      isDirty: false,
    },
  };

  // Transform content data (questions)
  const topics =
    widget.webChatWidgetQuestion?.map((q, index) => ({
      id: parseInt(q.id),
      title: (q.topic ?? q.question) || "",
      description: q?.metaData?.welcomeMessage ?? "",
      enabled: q.isActive !== undefined ? Boolean(q.isActive) : true,
      order: index + 1,
    })) || [];

  const content = {
    topics,
    isLoading: false,
    isSaving: false,
    hasUnsavedChanges: false,
  };

  // Transform preferences data
  const preferences = {
    trustedDomains: {
      domains: widget.webChatWidgetOrigin?.map(origin => origin.origin) || [],
      isLoading: false,
      error: null,
    },
    forwardConversation: {
      mode:
        widget.webChatUser?.length > 0 ? (widget.webChatUser[0].team ? "team" : "user") : "user",
      selectedUsers:
        widget.webChatUser?.filter(u => u.user && !u.team).map(u => parseInt(u.user.id)) || [],
      selectedTeams: widget.webChatUser?.filter(u => u.team).map(u => parseInt(u.team.id)) || [],
      isLoading: false,
      error: null,
    },
    conversationModes: {
      chatWithUs: metadata.showChatUsButton !== undefined ? metadata.showChatUsButton : true,
      textUs: metadata.showTextUsButton !== undefined ? metadata.showTextUsButton : true,
      isLoading: false,
      error: null,
    },
    askForLocation: {
      enabled: widget.webChatWidgetInitiationMethod?.code === INDICATION_METHOD_CODE.LOCATIONS,
      isLoading: false,
      error: null,
    },
    showLeadHelpWithOptions: {
      enabled: metadata.showLeadHelpWithOptions === true,
      isLoading: false,
      error: null,
    },
    isLoading: false,
    isSaving: false,
    hasUnsavedChanges: false,
  };

  return {
    appearance,
    content,
    preferences,
  };
};
